/**
 * Asset Data Service
 * Unified service for managing built-in asset data with multi-level indexing
 */

import { AssetType, AssetSearchResult } from '@/types/api';

interface BuiltInAssetData {
  version: string;
  lastUpdated: string;
  assets: {
    crypto: BuiltInAsset[];
    stock: BuiltInAsset[];
    forex: BuiltInAsset[];
    index: BuiltInAsset[];
  };
}

interface BuiltInAsset {
  id: string;
  symbol: string;
  name: string;
  rank?: number;
  exchange?: string;
}

interface SearchOptions {
  matchMode?: 'exact' | 'fuzzy' | 'contains';
  maxResults?: number;
  relevanceThreshold?: number;
}

interface SearchResultWithRelevance extends AssetSearchResult {
  relevance: number;
}

/**
 * Calculate relevance score for search results
 */
function calculateRelevance(query: string, asset: AssetSearchResult): number {
  const lowerQuery = query.toLowerCase().trim();
  const lowerSymbol = asset.symbol.toLowerCase();
  const lowerName = asset.name.toLowerCase();
  
  // Exact symbol match: 100 points
  if (lowerSymbol === lowerQuery) return 100;
  
  // Symbol starts with query: 80 points
  if (lowerSymbol.startsWith(lowerQuery)) return 80;
  
  // Symbol contains query: 60 points
  if (lowerSymbol.includes(lowerQuery)) return 60;
  
  // Exact name match: 50 points
  if (lowerName === lowerQuery) return 50;
  
  // Name starts with query: 40 points
  if (lowerName.startsWith(lowerQuery)) return 40;
  
  // Name contains query: 30 points
  if (lowerName.includes(lowerQuery)) return 30;
  
  // Word boundary match in name: 20 points
  const nameWords = lowerName.split(/\s+/);
  if (nameWords.some(word => word.startsWith(lowerQuery))) return 20;
  
  return 0;
}

/**
 * Asset Data Service - Singleton pattern
 * Manages built-in asset data with multi-level indexing for fast search
 */
export class AssetDataService {
  private static instance: AssetDataService;
  
  // Multi-level indexes
  private indexes: {
    byType: Map<AssetType, AssetSearchResult[]>;
    bySymbol: Map<string, AssetSearchResult>;
    byName: Map<string, AssetSearchResult[]>;
  };
  
  private allAssets: AssetSearchResult[] = [];
  private initialized = false;
  
  private constructor() {
    this.indexes = {
      byType: new Map(),
      bySymbol: new Map(),
      byName: new Map()
    };
  }
  
  /**
   * Get singleton instance
   */
  static getInstance(): AssetDataService {
    if (!AssetDataService.instance) {
      AssetDataService.instance = new AssetDataService();
    }
    return AssetDataService.instance;
  }
  
  /**
   * Initialize service with built-in asset data
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    
    try {
      // Load built-in assets from JSON file
      // Using dynamic import for JSON in Vite
      const data = (await import('../data/builtInAssets.json')).default as BuiltInAssetData;
      
      // Convert to AssetSearchResult format
      const allAssets: AssetSearchResult[] = [];
      
      // Process each asset type
      for (const [type, assets] of Object.entries(data.assets)) {
        const assetType = type as AssetType;
        const typeAssets: AssetSearchResult[] = [];
        
        for (const asset of assets) {
          const searchResult: AssetSearchResult = {
            id: asset.id,
            symbol: asset.symbol,
            name: asset.name,
            type: assetType,
            ...(asset.exchange && { exchange: asset.exchange })
          };
          
          allAssets.push(searchResult);
          typeAssets.push(searchResult);
          
          // Index by symbol (case-insensitive)
          const symbolKey = asset.symbol.toUpperCase();
          this.indexes.bySymbol.set(symbolKey, searchResult);
          
          // Index by name (case-insensitive, multiple assets can have similar names)
          const nameKey = asset.name.toLowerCase();
          if (!this.indexes.byName.has(nameKey)) {
            this.indexes.byName.set(nameKey, []);
          }
          this.indexes.byName.get(nameKey)!.push(searchResult);
        }
        
        // Index by type
        this.indexes.byType.set(assetType, typeAssets);
      }
      
      this.allAssets = allAssets;
      this.initialized = true;
      
      console.log(`AssetDataService initialized with ${allAssets.length} built-in assets`);
    } catch (error) {
      console.error('Failed to initialize AssetDataService:', error);
      // Initialize with empty data to prevent errors
      this.initialized = true;
    }
  }

  /**
   * Merge extra assets into current dataset and rebuild indexes.
   * Intended for adding cached dictionaries (HK/A full list) for offline search.
   */
  async mergeAndUpdateAssets(extraAssets: AssetSearchResult[]): Promise<void> {
    if (!this.initialized) {
      await this.initialize();
    }
    if (!extraAssets || extraAssets.length === 0) return;

    const merged: AssetSearchResult[] = [];
    const seen = new Set<string>();

    const add = (a: AssetSearchResult) => {
      const key = `${a.symbol.toUpperCase()}-${a.type}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(a);
    };

    // Built-in assets first (higher priority)
    for (const a of this.allAssets) add(a);
    // Then dictionary assets
    for (const a of extraAssets) add(a);

    this.updateAssets(merged);
  }
  
  /**
   * Search assets with relevance scoring
   */
  search(
    query: string,
    type?: AssetType,
    options: SearchOptions = {}
  ): AssetSearchResult[] {
    if (!this.initialized) {
      console.warn('AssetDataService not initialized, returning empty results');
      return [];
    }
    
    const {
      matchMode = 'contains',
      maxResults = 50,
      relevanceThreshold = 0
    } = options;
    
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return [];
    }
    
    // Get candidate assets based on type filter
    const candidates = type
      ? (this.indexes.byType.get(type) || [])
      : this.allAssets;
    
    const lowerQuery = trimmedQuery.toLowerCase();
    
    // Step 1: Quick filter - fast string matching before relevance calculation
    // This reduces the number of assets we need to process
    const quickMatches = candidates.filter(asset => {
      const lowerSymbol = asset.symbol.toLowerCase();
      const lowerName = asset.name.toLowerCase();
      
      // Fast contains check using indexOf (faster than includes for most engines)
      const symbolMatch = lowerSymbol.indexOf(lowerQuery) !== -1;
      const nameMatch = lowerName.indexOf(lowerQuery) !== -1;
      
      // Apply match mode filter
      switch (matchMode) {
        case 'exact':
          return lowerSymbol === lowerQuery || lowerName === lowerQuery;
        case 'fuzzy':
        case 'contains':
          return symbolMatch || nameMatch;
        default:
          return true;
      }
    });
    
    // Step 2: Limit candidates before relevance calculation for large datasets
    // For large queries, we can limit to a reasonable number before calculating relevance
    const candidatesToProcess = quickMatches.length > maxResults * 3
      ? quickMatches.slice(0, maxResults * 3)
      : quickMatches;
    
    // Step 3: Calculate relevance only for filtered candidates
    const resultsWithRelevance: SearchResultWithRelevance[] = candidatesToProcess
      .map(asset => ({
        ...asset,
        relevance: calculateRelevance(trimmedQuery, asset)
      }))
      .filter(result => {
        // Filter by relevance threshold
        return result.relevance >= relevanceThreshold;
      })
      .sort((a, b) => {
        // Sort by relevance (descending), then by rank if available
        if (b.relevance !== a.relevance) {
          return b.relevance - a.relevance;
        }
        // If same relevance, prefer assets with rank (lower rank = higher priority)
        const aRank = this.getAssetRank(a);
        const bRank = this.getAssetRank(b);
        if (aRank !== undefined && bRank !== undefined) {
          return aRank - bRank;
        }
        if (aRank !== undefined) return -1;
        if (bRank !== undefined) return 1;
        return 0;
      });
    
    // Limit results
    const limitedResults = resultsWithRelevance.slice(0, maxResults);
    
    // Return without relevance score
    return limitedResults.map(({ relevance, ...asset }) => asset);
  }
  
  /**
   * Get asset by symbol (exact match)
   */
  getBySymbol(symbol: string, type?: AssetType): AssetSearchResult | null {
    if (!this.initialized) {
      return null;
    }
    
    const symbolKey = symbol.toUpperCase();
    const asset = this.indexes.bySymbol.get(symbolKey);
    
    if (!asset) {
      return null;
    }
    
    // Filter by type if specified
    if (type && asset.type !== type) {
      return null;
    }
    
    return asset;
  }
  
  /**
   * Get assets by type
   */
  getByType(type: AssetType): AssetSearchResult[] {
    if (!this.initialized) {
      return [];
    }
    
    return this.indexes.byType.get(type) || [];
  }
  
  /**
   * Get all assets
   */
  getAll(): AssetSearchResult[] {
    if (!this.initialized) {
      return [];
    }
    
    return [...this.allAssets];
  }
  
  /**
   * Update assets (for dynamic updates)
   */
  updateAssets(assets: AssetSearchResult[]): void {
    // Clear existing indexes
    this.indexes.byType.clear();
    this.indexes.bySymbol.clear();
    this.indexes.byName.clear();
    
    // Rebuild indexes
    const byType = new Map<AssetType, AssetSearchResult[]>();
    
    for (const asset of assets) {
      // Index by type
      if (!byType.has(asset.type)) {
        byType.set(asset.type, []);
      }
      byType.get(asset.type)!.push(asset);
      
      // Index by symbol
      const symbolKey = asset.symbol.toUpperCase();
      this.indexes.bySymbol.set(symbolKey, asset);
      
      // Index by name
      const nameKey = asset.name.toLowerCase();
      if (!this.indexes.byName.has(nameKey)) {
        this.indexes.byName.set(nameKey, []);
      }
      this.indexes.byName.get(nameKey)!.push(asset);
    }
    
    this.indexes.byType = byType;
    this.allAssets = assets;
  }
  
  /**
   * Load assets from file (for future use)
   */
  async loadFromFile(path: string): Promise<void> {
    try {
      const response = await fetch(path);
      if (!response.ok) {
        throw new Error(`Failed to load assets from ${path}: ${response.statusText}`);
      }
      
      const data: BuiltInAssetData = await response.json();
      
      // Convert and update
      const allAssets: AssetSearchResult[] = [];
      
      for (const [type, assets] of Object.entries(data.assets)) {
        const assetType = type as AssetType;
        
        for (const asset of assets) {
          allAssets.push({
            id: asset.id,
            symbol: asset.symbol,
            name: asset.name,
            type: assetType,
            ...(asset.exchange && { exchange: asset.exchange })
          });
        }
      }
      
      this.updateAssets(allAssets);
    } catch (error) {
      console.error(`Failed to load assets from ${path}:`, error);
      throw error;
    }
  }
  
  /**
   * Get asset rank (helper method)
   */
  private getAssetRank(asset: AssetSearchResult): number | undefined {
    // Try to find the asset in the original data to get rank
    // For now, we'll use a simple heuristic based on symbol length and common patterns
    // This can be enhanced later with actual rank data
    return undefined;
  }
  
  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
  
  /**
   * Get statistics
   */
  getStats(): {
    totalAssets: number;
    assetsByType: Record<AssetType, number>;
  } {
    const assetsByType: Record<AssetType, number> = {
      crypto: 0,
      stock: 0,
      index: 0,
      forex: 0,
      bond: 0,
      futures: 0,
      etf: 0,
      fund: 0
    };
    
    for (const [type, assets] of this.indexes.byType.entries()) {
      assetsByType[type] = assets.length;
    }
    
    return {
      totalAssets: this.allAssets.length,
      assetsByType
    };
  }
}

// Export singleton instance
export const assetDataService = AssetDataService.getInstance();
